import React, { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import KPICardWithTrend from "../KPICardWithTrend";
import RevenueTrendChart from "./RevenueTrendChart";
import CPTCodesTable from "./CPTCodesTable";
import styles from "./FinancialOverview.module.css";
import MonthYearPicker from "../../shared-modules/DatePicker/MonthYearPicker";
import { getFinancialOverviewKpis, getRevenueTrend, getCPTCodesGenerated } from "../../../services/super-admin-dashboard/financialOverview";

const FinancialOverview = ({ scope = "all", title = "Client Billing Overview" }) => {
  const [selectedMonthYear, setSelectedMonthYear] = useState("");
  const [cptCategory, setCptCategory] = useState("");

  const { data: kpis, isLoading } = useQuery({
    queryKey: ["financial-overview-kpis", scope, selectedMonthYear],
    queryFn: () => getFinancialOverviewKpis(scope, selectedMonthYear || undefined),
    staleTime: 0,
    cacheTime: 0,
    refetchOnMount: "always",
  });

  const { data: revenueTrend, isLoading: isRevenueTrendLoading } = useQuery({
    queryKey: ["financial-overview-revenue-trend", scope, selectedMonthYear],
    queryFn: () => getRevenueTrend(scope, selectedMonthYear || undefined),
    staleTime: 0,
    cacheTime: 0,
    refetchOnMount: "always",
  });

  const { data: cptCodes, isLoading: isCPTCodesLoading } = useQuery({
    queryKey: ["financial-overview-cpt-codes", scope, selectedMonthYear, cptCategory],
    queryFn: () => getCPTCodesGenerated(scope, selectedMonthYear || undefined, cptCategory || undefined),
    staleTime: 0,
    cacheTime: 0,
    refetchOnMount: "always",
  });

  const formatCurrency = (value) => {
    if (!value) return "$0";
    return `$${Number(value).toLocaleString()}`;
  };

  return (
    <div className={styles.sectionCard}>
      <div className={styles.headerRow}>
        <h2 className={styles.title}>{title}</h2>
        <MonthYearPicker value={selectedMonthYear} onChange={setSelectedMonthYear} />
      </div>
      <div className={styles.divider} />

      <div className={styles.kpiGrid}>
        <KPICardWithTrend
          title="Total Billed Revenue"
          value={isLoading ? "..." : formatCurrency(kpis?.total_billed_revenue?.value ?? 0)}
          className={styles.kpiGreen}
          isLoading={isLoading}
        />
        <KPICardWithTrend
          title="Total Paid Revenue"
          value={isLoading ? "..." : formatCurrency(kpis?.total_paid_revenue?.value ?? 0)}
          className={styles.kpiGreen}
          isLoading={isLoading}
        />
        <KPICardWithTrend
          title="Billable Patients"
          value={isLoading ? "..." : String(kpis?.billable_patients?.value ?? 0)}
          className={styles.kpiBlue}
          isLoading={isLoading}
        />
        <KPICardWithTrend
          title="Avg Revenue/Patient"
          value={isLoading ? "..." : formatCurrency(kpis?.avg_revenue_per_patient?.value ?? 0)}
          className={styles.kpiCyan}
          isLoading={isLoading}
        />
          <KPICardWithTrend
       title="Billing Success Rate"
      value={isLoading ? "..." : `${(Number(kpis?.billing_success_rate?.value ?? 0) * 100).toLocaleString()}%`}
       className={styles.kpiCyan}
       isLoading={isLoading}
/>

        <KPICardWithTrend
          title="Appealed Claims"
          value={isLoading ? "..." : String(kpis?.appealed_claims?.value ?? 0)}
          className={styles.kpiGray}
          isLoading={isLoading}
        />
      </div>

      <div className={styles.chartsGrid}>
        <RevenueTrendChart
          data={revenueTrend || []}
          title="Revenue Trend (RPM vs CCM)"
          isLoading={isRevenueTrendLoading}
        />
        <CPTCodesTable
          data={cptCodes || []}
          title="CPT Codes Generated"
          isLoading={isCPTCodesLoading}
          categoryFilter={cptCategory}
          onCategoryChange={setCptCategory}
        />
      </div>
    </div>
  );
};

export default FinancialOverview;
